/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { cn } from '@/utils/css'
import React, { createElement, useEffect, useState } from 'react'
import { IconType } from 'react-icons'
import { MdCheckCircle, MdError, MdInfo, MdOutlineWarning } from 'react-icons/md'

/** A description of the available properties for this component. */
type AlertProps = {
  /** The type of alert to display. */
  type: 'success' | 'error' | 'warning' | 'info'

  /** The message to display in the alert. */
  message: string | React.ReactNode

  /** Optional title for the alert. */
  title?: string

  /** Optional class name to apply to the alert. */
  className?: string

  /** The duration in milliseconds to show the alert. */
  duration?: number
}

/** A map of alert types to their corresponding tailwind colours. */
const alertTypeClasses: Record<AlertProps['type'], string> = {
  success: 'success',
  error: 'danger',
  warning: 'warning',
  info: 'info',
}

/** A map of alert types to their corresponding icons. */
const alertTypeIcons: Record<AlertProps['type'], IconType> = {
  success: MdCheckCircle,
  error: MdError,
  warning: MdOutlineWarning,
  info: MdInfo,
}

/**
 * A component to display an alert to the user.
 */
const Alert: React.FC<AlertProps> = ({ type, message, title, className, duration }) => {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    if (duration) {
      const timer = setTimeout(() => {
        setIsVisible(false)
      }, duration)

      return () => clearTimeout(timer)
    }
  }, [duration])

  if (!isVisible) {
    return null
  }

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl p-4',
        `bg-${alertTypeClasses[type]}-50`,
        className,
        `${className}`,
      )}
      role={'alert'}
    >
      {duration && (
        <div
          className={`absolute bottom-0 left-0 h-1 w-full bg-${alertTypeClasses[type]}-500 animate-out slide-out-to-left-full ease-linear`}
          style={{
            animationDuration: `${duration ? duration / 1000 : 0}s`,
          }}
        ></div>
      )}
      <div className={'flex items-center'}>
        <div className={'shrink-0'}>
          {createElement(alertTypeIcons[type], { className: `h-5 w-5 text-${alertTypeClasses[type]}-500` })}
        </div>
        <div className={'ml-3'}>
          {title && <h3 className={`text-lg font-medium text-${alertTypeClasses[type]}-800`}>{title}</h3>}
          {typeof message === 'string' ? <p className={`text-base font-medium text-grey-450`}>{message}</p> : message}
        </div>
      </div>
    </div>
  )
}

export { Alert }
